// create a new component for the inventory edit advice
import React from "react";
import { Grid, InputAdornment, Typography } from "@material-ui/core";
import {
  TextInput,
  required,
  number,
  minValue,
  NumberInput,
  SelectInput,
  ArrayField,
  Datagrid,
  FunctionField,
  TextField,
  NumberField,
} from "react-admin";
import { CustomFields } from "../../../../components/common/CustomFields";
import { isAuthorized } from "../../../../utils/appUtils";
import {
  AclType,
  InventoryField,
  InventoryStatus,
  UnitWeightOptions,
} from "../../../../utils/constants";
import {
  getLocationFullName,
  isRailShipmentInTrackArea,
} from "../../../../utils/locationUtils";
import {
  getUnitName,
  isInventoryFieldVisible,
  isPieceDimensionsVisible,
  unitOptionRenderer,
} from "../../../../utils/loadTypeUtils";
import { exceptionRenderer, trimDecimal } from "../../../../utils/utils";
import { AuditStatusIcon } from "../AuditStatusIcon";
import { getInfoBubble, showWeight } from "../InventoryUitls";
import { LinkProductDialog } from "../LinkProductDialog";

export const InventoryEditAdvice = ({
  backPath,
  productIsOpen,
  setProductIsOpen,
  formData,
  record,
  handleQuantityAdvisedChange,
  validateQty,
  props,
  handleProductLink,
  inventoryItems,
  ListActionButtons,
}) => {
  const yardCommodities = window.appUser.reloadYard.activeYardCommodities.items;

  const isOnHand = (fd) =>
    fd.status !== InventoryStatus.IN_TRANSIT &&
    fd.status !== InventoryStatus.ARRIVED;

  const getQtyLabel = (fd) => (isOnHand(fd) ? "Qty. On Hand" : "Putaway Qty.");

  const renderUnits = () => getUnitName(record.inventoryUOM);

  const getWeightHelperText = (fd) => {
    if (fd.weight && fd.quantityOnHand) {
      const formatter = new Intl.NumberFormat("en-US");
      return `Net: ${formatter.format(
        trimDecimal(fd.weight * fd.quantityOnHand, 3),
      )} ${fd.weightUOM}`;
    }
    return false;
  };

  const validateRequired = required();
  const validateQtyRequired = [required(), number(), minValue(0)];
  const validatePieces = [number(), minValue(1)];
  const validateDimensions = [number(), minValue(0)];
  return (
    <Grid container direction="column" spacing={0}>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} md={12}>
          <TextInput
            source="productName"
            label="Product Name"
            variant="outlined"
            fullWidth
            validate={validateRequired}
            helperText={false}
            InputProps={{
              endAdornment: getInfoBubble(formData),
            }}
          />
        </Grid>
      </Grid>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12}>
          <TextInput
            source="braRawProductName"
            label="OCR Product Name"
            variant="outlined"
            fullWidth
            disabled={true}
            helperText={false}
          />
        </Grid>
      </Grid>
      {record.status !== InventoryStatus.STORED &&
        record.status !== InventoryStatus.SHIPPED && (
          <Grid container item direction="row" spacing={1}>
            <Grid item xs={12} md={6}>
              <NumberInput
                source="quantityAdvised"
                label="Quantity Advised"
                variant="outlined"
                fullWidth
                disabled={
                  formData.status === InventoryStatus.STORED ||
                  formData.status === InventoryStatus.SHIPPED ||
                  formData.status === InventoryStatus.RELEASED
                }
                min="0"
                onChange={handleQuantityAdvisedChange}
                validate={validateQtyRequired}
                error={validateQty(formData).quantityAdvised}
                helperText={
                  record?.quantityPrecount != null
                    ? `Pre-count: ${record.quantityPrecount}`
                    : validateQty(formData).quantityAdvised || false
                }
                FormHelperTextProps={{
                  style: {
                    color:
                      record?.quantityPrecount != null &&
                      formData?.quantityAdvised !== record.quantityPrecount
                        ? "#f44336"
                        : undefined,
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {" "}
                      {getUnitName(record.inventoryUOM)}{" "}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {isInventoryFieldVisible(
              InventoryField.PCS,
              record.reloadYardCommodity?.commodity,
            ) && (
              <Grid item xs={12} md={6}>
                <NumberInput
                  source="pcs"
                  label="Pieces / Unit"
                  variant="outlined"
                  fullWidth
                  min="1"
                  placeholder={record.product?.pcs}
                  InputLabelProps={{
                    shrink:
                      formData?.pcs != null || record?.product?.pcs != null, //not null or undefined
                  }}
                  validate={validatePieces}
                  helperText={false}
                />
              </Grid>
            )}
          </Grid>
        )}
      {(record.status === InventoryStatus.STORED ||
        record.status === InventoryStatus.SHIPPED) && (
        <Grid container item direction="row" spacing={1}>
          <Grid item xs={12} md={6}>
            <NumberInput
              source="quantityOnHand"
              label="Quantity On Hand"
              variant="outlined"
              fullWidth
              min="0"
              validate={
                record.status === InventoryStatus.STORED
                  ? validateQtyRequired
                  : undefined
              }
              error={validateQty(formData).quantityOnHand}
              helperText={validateQty(formData).quantityOnHand}
              disabled={
                formData.status === InventoryStatus.STORED ||
                formData.status === InventoryStatus.SHIPPED
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {" "}
                    {getUnitName(record.inventoryUOM)}{" "}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          {record.quantityMismatch && (
            <Grid item xs={12} md={6}>
              <NumberInput
                source="quantityPhysical"
                label="Quantity Physical"
                variant="outlined"
                fullWidth
                min="0"
                error={validateQty(formData).quantityPhysical}
                helperText={validateQty(formData).quantityPhysical}
                disabled={
                  formData.status !== InventoryStatus.STORED ||
                  formData.status === InventoryStatus.SHIPPED
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {" "}
                      <AuditStatusIcon record={formData} {...props} />{" "}
                      {getUnitName(record.inventoryUOM)}{" "}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          )}
          {isInventoryFieldVisible(
            InventoryField.PCS,
            record.reloadYardCommodity?.commodity,
          ) && (
            <Grid item xs={12} md={6}>
              <NumberInput
                source="pcs"
                label="Pieces / Unit"
                variant="outlined"
                fullWidth
                min="1"
                placeholder={record.product?.pcs}
                InputLabelProps={{
                  shrink: formData?.pcs != null || record?.product?.pcs != null,
                }}
                validate={validatePieces}
                helperText={false}
              />
            </Grid>
          )}
        </Grid>
      )}
      {(record.status === InventoryStatus.STORED ||
        record.status === InventoryStatus.SHIPPED) && (
        <Grid container direction="row" spacing={1}>
          <Grid item xs={12} md={6}>
            <NumberInput
              source="quantityCommitted"
              label="Quantity Committed"
              variant="outlined"
              fullWidth
              disabled={true}
              helperText={false}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {" "}
                    {getUnitName(record.inventoryUOM)}{" "}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <NumberInput
              source="quantityAvailable"
              label="Quantity Available"
              variant="outlined"
              fullWidth
              // defaultValue={formData.quantityOnHand - formData.quantityCommitted}
              format={() =>
                trimDecimal(
                  formData.quantityOnHand - formData.quantityCommitted,
                  3,
                )
              }
              disabled={true}
              helperText={false}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {" "}
                    {getUnitName(record.inventoryUOM)}{" "}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      )}
      {showWeight(record.account, record) && (
        <Grid container direction="row" spacing={1}>
          <Grid item xs={12} md={6}>
            <NumberInput
              source="weight"
              label={`Weight: ${formData.weightUOM} / ${formData.inventoryUOM}`}
              variant="outlined"
              fullWidth
              disabled={
                formData.status === InventoryStatus.SHIPPED ? true : false
              }
              validate={formData?.weightUOM ? validateRequired : undefined}
              helperText={getWeightHelperText(formData)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SelectInput
              source="weightUOM"
              label="Weight UOM"
              variant="outlined"
              fullWidth
              allowEmpty
              choices={UnitWeightOptions}
              optionText={unitOptionRenderer}
              disabled={
                formData.status === InventoryStatus.SHIPPED ? true : false
              }
              helperText={false}
            />
          </Grid>
        </Grid>
      )}
      {productIsOpen && (
        <Grid container spacing={0}>
          <Grid item xs={false}>
            <LinkProductDialog
              {...props}
              record={formData}
              backPath={backPath}
              isOpen={productIsOpen}
              setIsOpen={setProductIsOpen}
              handleAddSelected={handleProductLink}
            />
          </Grid>
        </Grid>
      )}
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} md={6}>
          <SelectInput
            source="reloadYardCommodityId"
            label="Load Type"
            variant="outlined"
            fullWidth
            disabled={record.productId || inventoryItems?.items.length > 0}
            choices={yardCommodities}
            optionText="commodity.name"
            optionValue="id"
            validate={validateRequired}
            helperText={false}
          />
        </Grid>
      </Grid>
      {isPieceDimensionsVisible(record.reloadYardCommodity?.commodity) && (
        <Grid container direction="row" spacing={1}>
          <Grid item xs={12} style={{ paddingBottom: 0 }}>
            <Typography variant="overline" color="textSecondary">
              PIECE DIMENSIONS
            </Typography>
          </Grid>
          {isInventoryFieldVisible(
            InventoryField.HEIGHT,
            record.reloadYardCommodity?.commodity,
          ) && (
            <Grid item xs={12} md={4}>
              <NumberInput
                source="pcsHeight"
                label="Piece Height"
                variant="outlined"
                fullWidth
                placeholder={record?.product?.pcsHeight}
                InputLabelProps={{
                  shrink:
                    record?.product?.pcsHeight != null ||
                    formData?.pcsHeight != null, //not null or undefined
                }}
                validate={validateDimensions}
                helperText={false}
                InputProps={{
                  inputMode: "decimal",
                  endAdornment: (
                    <InputAdornment position="end">IN</InputAdornment>
                  ),
                }}
              />
            </Grid>
          )}
          {isInventoryFieldVisible(
            InventoryField.WIDTH,
            record.reloadYardCommodity?.commodity,
          ) && (
            <Grid item xs={12} md={4}>
              <NumberInput
                source="pcsWidth"
                label="Pice Width"
                variant="outlined"
                fullWidth
                placeholder={record.product?.pcsWidth}
                InputLabelProps={{
                  shrink:
                    record?.product?.pcsWidth != null ||
                    formData?.pcsWidth != null, //not null or undefined
                }}
                validate={validateDimensions}
                helperText={false}
                InputProps={{
                  inputMode: "decimal",
                  endAdornment: (
                    <InputAdornment position="end">IN</InputAdornment>
                  ),
                }}
              />
            </Grid>
          )}
          {isInventoryFieldVisible(
            InventoryField.LENGTH,
            record.reloadYardCommodity?.commodity,
          ) && (
            <Grid item xs={12} md={4}>
              <NumberInput
                source="pcsLength"
                label="Length"
                variant="outlined"
                fullWidth
                placeholder={record.product?.pcsLength || formData?.pcsLength}
                InputLabelProps={{
                  shrink:
                    record?.product?.pcsLength != null ||
                    formData?.pcsLength != null, //not null or undefined
                }}
                validate={validateDimensions}
                helperText={false}
                InputProps={{
                  inputMode: "decimal",
                  endAdornment: (
                    <InputAdornment position="end">FT</InputAdornment>
                  ),
                }}
              />
            </Grid>
          )}
        </Grid>
      )}
      <Grid container direction="row" spacing={1}>
        <CustomFields
          {...props}
          recordFields={record.customFields}
          accountFields={record.account.inventoryCustomFields}
          width={4}
          disabled={false}
        />
        <Grid item container direction="row" spacing={1}>
          <Grid item xs={12}>
            <ArrayField source="items" record={inventoryItems}>
              <Datagrid>
                <FunctionField
                  source="hasException"
                  label=" "
                  render={exceptionRenderer}
                />
                <FunctionField
                  label="Location"
                  render={(record) =>
                    getLocationFullName(
                      record.location || record.locationFullName,
                    )
                  }
                />
                {isRailShipmentInTrackArea(
                  formData.areaId,
                  record.inboundShipment,
                ) && (
                  <TextField
                    source="inventory.railCarNumber"
                    label="Car Number"
                  />
                )}

                <NumberField
                  source="quantityOnHand"
                  label={getQtyLabel(formData)}
                />
                {isOnHand(formData) && (
                  <NumberField
                    source="quantityCommitted"
                    label="Qty. Committed"
                  />
                )}
                <FunctionField label="UOM" render={renderUnits} />
                {formData.status !== InventoryStatus.IN_TRANSIT && (
                  <TextField source="unloadedBy" label="Unloaded By" />
                )}
                {isAuthorized(AclType.ASSIGN_LOCATION) && <ListActionButtons />}
              </Datagrid>
            </ArrayField>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default InventoryEditAdvice;
